import React, { useEffect } from "react"
import { useState } from "react"
import Header from "../../../components/navigation/Header"
import Footer from "../../../components/navigation/Footer"
import GetCSFR from "../../../hooks/userAuthentication/GetCSFR"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLongArrowLeft } from "@fortawesome/free-solid-svg-icons"
import data from "../../../data.json"
import LoadingAnimation from "../../../components/general/LoadingAnimation"
import { useNavigate, useSearchParams } from "react-router-dom"
import axios from "axios"

interface Transaction {
    coach: string;
    sport: string;
    amount: number;
    date: string;
    transactionID: number;
}

interface Summary {
    totalBookings: number;
    totalRevenue: number;
    totalCoachesBooked: number;
}

interface Report {
    type: string;
    sport: string;
    startDate: string;
    endDate: string;
}

export default function FinancialReport(){
    const csrfToken = GetCSFR({ name: "csrftoken" })
    const [loading, setLoading] = useState<boolean>(false)
    const navigate = useNavigate()
    const [transactions, setTransactions] = useState<Transaction[] | null>(null)
    const [summary, setSummary] = useState<Summary | null>(null)
    const [searchParams] = useSearchParams()
    const dateParam = searchParams.get("date")
    const sports = data.sports
    const [reportFilters, setReportFilters] = useState({
        type: "all",
        sport: "all",
        startDate: "",
        endDate: "",
    })
    const [failedReport, setFailedReport] = useState<string | null>(null)

    useEffect(()=>{
        document.title = 'SkillJa - Financial Report'

        if (!dateParam) return
        console.log(dateParam)
        const dateFilters = formatUrlParameter(dateParam)
        if (!dateFilters) return
        // if date parameter exists, then quick link was clicked in previous page so run report on render
        runReport({
            type: "all",
            sport: "all",
            startDate: dateFilters.startDate,
            endDate: dateFilters.endDate,
        })
    },[dateParam])

    // handle input changes
    function handleChange(e:any) {
        const { name, value } = e.target

        setReportFilters((prev) => ({
            ...prev,
            [name]: value,
        }))
    }

    function toLocalDateString(date: Date) {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, "0")
        const day = String(date.getDate()).padStart(2, "0")

        return `${year}-${month}-${day}`
    }


    // Format url date parameter
    function formatUrlParameter(param:string){
        const today = new Date()

        const yesterday = new Date(today)
        yesterday.setDate(today.getDate() - 1)

        const lastSevenDays = new Date(today)
        lastSevenDays.setDate(today.getDate() - 7)

        const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1)

        if (param === "yesterday") {
            return { startDate: toLocalDateString(yesterday), endDate: toLocalDateString(today) }
        }
        if (param === "lastsevendays") {
            return { startDate: toLocalDateString(lastSevenDays), endDate: toLocalDateString(today) }
        }
        if (param === "currentmonth") {
            return { startDate: toLocalDateString(firstDayOfCurrentMonth), endDate: toLocalDateString(today) }
        }
    }

    // reset filters from report inputs
    function resetInputs(){
        setReportFilters({type: "all",
            sport: "all",
            startDate: "",
            endDate: "",
        })
    }

    // API call to retrieve all transactions and summary
    function runReport(filters:Report){
        if (!filters) return

        setLoading(true)

        const params = new URLSearchParams({
            type: filters.type,
            sport: filters.sport,
            start_date: filters.startDate,
            end_date: filters.endDate,
        })

        axios.get(`${process.env.REACT_APP_SKILLJA_URL}/club/financial_report/?${params.toString()}`, { 
            headers: {
                'X-CSRFToken': csrfToken,
                'Content-Type': 'application/json'
            },
            withCredentials: true
            }) 
            .then(res => {
                if(res.status===200){
                    setTransactions(res.data.transactions)
                    setSummary(res.data.summary)
                    setFailedReport(null)
                } 
                else if (res.status === 400){
                    setFailedReport(res.data.error)
                }
            })
            .catch(error => {console.error(error)})
            .finally(()=>setLoading(false))
    }

    return (
        <div className="flex flex-col">
            <Header useCase="protected" />
            <section className="flex flex-col flex-wrap font-kulim mt-10 px-8 lg:px-32 xl:px-40 mb-16 lg:mb-32">
                <div className="grid grid-cols-3 mb-8">
                    <FontAwesomeIcon icon={faLongArrowLeft} className="text-3xl my-auto hover:text-main-green-500 cursor-pointer justify-self-start" onClick={()=>navigate(-1)}/>
                    <h2 className="font-kulim text-2xl sm:text-2xl lg:text-3xl text-center text-main-green-900 font-semibold">
                        Financial Report
                    </h2>
                    <p className="w-6 ml-auto mr-0"></p>
                </div>
                <section className="border border-main-grey-100 font-kulim bg-white rounded-xl pt-2 pb-8 px-4">
                    <div className="flex flex-col justify-between md:flex-row md:flex-wrap pt-6 px-4">
                        <div className="flex mb-2 md:mb-0">
                            <label htmlFor="type" className="mr-2 my-auto">
                                Type
                            </label>
                            <select 
                                name="type" 
                                id="type" 
                                className="border rounded-2xl py-1 px-2 text-sm form-input"
                                value={reportFilters.type}
                                onChange={handleChange}
                            >
                                <option value="all">All</option>
                                <option value="approved">Approved Lessons</option>
                                <option value="cancelled">Cancelled Lessons</option>
                            </select>
                        </div>
                        <div className="flex mb-2 md:mb-0">
                            <label htmlFor="sport" className="md:ml-10 mr-2 my-auto">
                                Sport
                            </label>
                            <select 
                                name="sport" 
                                id="sport" 
                                className="border rounded-2xl py-1 px-2 text-sm form-input"
                                value={reportFilters.sport}
                                onChange={handleChange}
                            >
                                <option value="all">All</option>
                                {sports.map((sport, index) => (
                                    <option key={index} value={sport}>
                                        {sport}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex z-0 md:ml-auto mb-2 xl:mb-0">
                            <label htmlFor="startDate" className="md:ml-5 lg:ml-8 mr-2 my-auto">
                                Start Date
                            </label>
                            <div className="flex w-[10.5rem] relative">
                                <input
                                    name="startDate"
                                    type="date"
                                    value={reportFilters.startDate}
                                    onChange={handleChange}
                                    className="date-input text-sm !px-0"
                                />
                            </div>
                        </div>
                        <div className="flex z-0 md:ml-auto mb-2 xl:mb-0">
                            <label htmlFor="endDate" className="md:ml-5 lg:ml-8 mr-2 my-auto">
                                End Date
                            </label>
                            <div className="flex w-[10.5rem] relative">
                                <input
                                    name="endDate"
                                    type="date"
                                    value={reportFilters.endDate}
                                    onChange={handleChange}
                                    className="date-input text-sm !px-0"
                                />
                            </div>
                        </div>
                        <div className="flex w-full mt-8 mb-2">
                            <div className="grid grid-cols-4 gap-1 ml-auto text-sm md:text-base">
                                <p className="underline cursor-pointer text-start hover:text-main-green-500">
                                    Yesterday
                                </p>
                                <p className="underline cursor-pointer text-start hover:text-main-green-500">
                                    Last 7 Days
                                </p>
                                <p className="underline cursor-pointer text-center hover:text-main-green-500">
                                    Current Month
                                </p>
                                <p className="underline cursor-pointer text-end hover:text-main-green-500">
                                    Last Month
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="flex pl-4 mt-8 mx-4 mb-10 pt-6 border-t border-main-grey-200">
                        <div className="w-full max-w-2xl mr-auto">
                            <ul className="flex flex-wrap justify-between list-disc gap-x-2 gap-y-2">
                                <li className="w-fit pr-4">{summary?.totalBookings ? summary.totalBookings : '0'} bookings</li>
                                <li className="w-fit pr-4">${summary?.totalRevenue ? summary.totalRevenue : '0'} total revenue</li>
                                <li className="w-fit pr-4">{summary?.totalCoachesBooked ? summary.totalCoachesBooked : '0'} coaches booked</li>
                            </ul>
                        </div>
                        <div className="flex flex-col md:flex-row md:justify-end ml-auto border-l border-main-grey-200 pl-8">
                            {/* <button className="form-btn bg-main-green-600 text-base mr-3 py-1 px-3 mb-1.5 md:mb-auto my-auto">
                                Export
                            </button> */}
                            <button className="form-btn bg-main-grey-200 hover:bg-main-grey-400 text-base mr-3 py-1 px-3 mb-1.5 md:mb-auto my-auto" onClick={()=>resetInputs()}>
                                Reset
                            </button>
                            <button className="form-btn text-base py-1 px-3 mb-1.5 md:mb-auto my-auto" onClick={()=>runReport(reportFilters)}>
                                Run Report
                            </button>
                        </div>
                    </div>
                    {loading ?
                        <div className="mt-24 mb-6">
                            <LoadingAnimation />
                        </div>
                    :   
                        (failedReport ?
                            failedReport
                        :
                        
                            <div className="bg-main-grey-100 rounded-2xl py-2 lg:px-4 lg:mx-4 mt-10">
                                <div className="grid grid-cols-5 px-3 py-3 mb-2 text-xs sm:text-sm md:text-base">
                                    <p>
                                        Coach
                                    </p>
                                    <p>
                                        Sport
                                    </p>
                                    <p>
                                        Amount
                                    </p>
                                    <p>
                                        Date
                                    </p>
                                    <p>
                                        Transaction ID
                                    </p>
                                </div>
                                <div className="mx-1 md:mx-2">
                                    {transactions && transactions.map((transaction, index)=>{
                                        return (
                                            <div key={index} className="grid grid-cols-5 bg-white px-1 md:px-3 py-2 rounded-xl mb-2 text-xs sm:text-sm md:text-base">
                                                <p>{transaction.coach}</p>
                                                <p>{transaction.sport}</p>
                                                <p>{transaction.amount}</p>
                                                <p>{transaction.date.slice(0,-14)}</p>
                                                <p>{transaction.transactionID}</p>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )
                    }
                    
                </section>
            </section>
            <Footer />
        </div>
    )
}